import React from 'react';
import FallbackEngineClient from '../../engine/FallbackEngineClient.mjs';
import settings from '../../settings.js';
import { isTypingEvent } from '../../utils.js';
import { START_FEN, normalizeFen, enginePosition } from './game.mjs';
import { saveEnginePreference, saveEngineSelection } from '../../enginePreference.js';

// Shared lifecycle for both existing analysis layouts.
export default class AnalysisController extends React.Component {
  componentDidMount() {
    this._mounted = true;
    this.engine = new FallbackEngineClient({
      selectedEngine: this.state.selectedEngine,
      url: `${process.env.PUBLIC_URL || ''}/engine/stockfish-18.0.8/stockfish-18-lite-single.js`,
      fallbackEnabled: settings.engine.ENGINE_JS_FALLBACK_ENABLED,
      fallbackUrl: `${process.env.PUBLIC_URL || ''}${settings.engine.ENGINE_JS_FALLBACK_URL}`,
      primaryName: settings.engine.ENGINE_NAME,
      primaryShortName: settings.engine.ENGINE_NAME_SHORT,
      fallbackName: settings.engine.ENGINE_JS_FALLBACK_NAME,
      fallbackShortName: settings.engine.ENGINE_JS_FALLBACK_NAME_SHORT,
      depth: settings.engine.ENGINE_DEPTH,
      multiPv: settings.engine.ENGINE_MULTIPV,
      onStatus: ({ status, message, engine, engineName, engineShortName, resetEvals, preserveEvals, failureReason, diagnostic }) => {
        if (!this._mounted) return;
        const failed = status === 'error' || status === 'no-moves';
        if (failed) {
          if (status === 'error') {
            console.warn('[Chessvision engine]', {
              engine, reason: failureReason || 'unknown', message, diagnostic,
              unlimited: Boolean(this.state.engineUnlimited),
              pageHidden: document.hidden,
            });
          }
          this.failedAnalysis = { position: this.analysisPosition, status, message };
        }
        this.setState(state => {
          const clearResults = status === 'no-moves' ||
            (resetEvals && !preserveEvals) ||
            state.evals.some(info => info && info.fen !== state.fen);
          return {
            engineKind: engine,
            engineName,
            engineShortName,
            engineStatus: status,
            engineError: message,
            ...(clearResults ? { bestMove: null, evals: [] } : {}),
            ...(failed ? { engineUnlimited: false } : {}),
          };
        });
      },
      onInfo: info => {
        if (!this._mounted || !this.state.engineOn || info.fen !== this.state.fen) return;
        this.setState(state => {
          if (!state.engineOn || info.fen !== state.fen) return null;
          // Keep the previous engine's results while fallback is loading, then
          // replace them together so different engines/depths are never mixed.
          const previous = state.evals.find(Boolean);
          const evals = previous && previous.engine !== info.engine ? [] : [...state.evals];
          if (!evals[info.index] || evals[info.index].depth <= info.depth) evals[info.index] = info;
          return { evals, bestMove: evals[0]?.pv[0] || null };
        });
      },
    });
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handleVisibility);
    this.getScore();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname || prevProps.location.search !== this.props.location.search) {
      const params = new URLSearchParams(this.props.location.search);
      const raw = params.get('fen') ?? this.props.match?.params.fen;
      this.initFen = normalizeFen(raw ?? START_FEN);
      this.setState({ orientation: params.get('color') === 'black' ? 'black' : 'white' });
      this.setFen(this.initFen);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    this.engine?.dispose();
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handleVisibility);
  }

  handleKeyDown = event => {
    if (isTypingEvent(event) || this.state.promotionDialog || this.state.menuAnchorEl || this.state.engineMenuAnchorEl) return;
    const actions = { ArrowLeft: () => this.undo(), ArrowRight: () => this.redo(), Home: () => this.moveToFront(), End: () => this.moveToEnd() };
    if (actions[event.key]) { event.preventDefault(); actions[event.key](); }
  };

  handlePageHide = () => {
    this.engine?.pause();
    if (this._mounted && this.state.engineOn) this.setState({ engineStatus: 'paused' });
  };
  handleVisibility = () => {
    if (document.hidden) this.handlePageHide();
    else this.getScore({ preserveEvals: true });
  };

  getEngineStatusTitle(status) {
    const message = this.state.engineError || this.state.positionError || status;
    const last = this.state.evals.find(info => info && info.fen === this.state.fen);
    if (!last) return message;
    const name = last.engine === 'js'
      ? settings.engine.ENGINE_JS_FALLBACK_NAME : settings.engine.ENGINE_NAME;
    return `${message} Last analysis: ${name}, depth ${last.depth}.`;
  }

  getScore({ preserveEvals = false } = {}) {
    if (!this.engine || !this._mounted) return;

    if (!this.state.engineOn) {
      this.engine.pause();
      this.setState({ engineUnlimited: false, bestMove: null, evals: [] });
      return;
    }

    const position = enginePosition(this.game, this.state.lastMoveId, this.state.fen);
    // Retry a failed position only after an explicit reload or off/on toggle.
    if (this.failedAnalysis?.position === position) {
      this.setState({
        engineStatus: this.failedAnalysis.status,
        engineError: this.failedAnalysis.message,
      });
      return;
    }

    const samePosition = position === this.analysisPosition;
    const unlimited = Boolean(this.state.engineUnlimited && samePosition);
    this.analysisPosition = position;
    this.setState({
      engineUnlimited: unlimited,
      engineError: '',
      ...(preserveEvals && samePosition ? {} : { bestMove: null, evals: [] }),
    });

    if (document.hidden) {
      this.engine.pause();
      this.setState({ engineStatus: 'paused' });
      return;
    }

    // Stockfish decides whether it can analyze, independently of chess.js.
    this.engine.analyze({ fen: this.state.fen, position, infinite: unlimited });
  }

  toggleEngine = () => {
    if (!this.state.engineOn) this.failedAnalysis = null;
    if (this.state.engineOn) this.engine?.pause();

    this.setState(
      state => ({ engineOn: !state.engineOn }),
      () => {
        saveEnginePreference(
          this.state.engineOn,
          this.rememberEnginePreference
        );

        this.getScore();
      }
    );
  };

  openEngineMenu = event => {
    this.setState({ engineMenuAnchorEl: event.currentTarget });
  };

  closeEngineMenu = () => {
    this.setState({ engineMenuAnchorEl: null });
  };

  selectEngine = selectedEngine => {
    if (!['classic', 'nnue'].includes(selectedEngine)) return;
    saveEngineSelection(selectedEngine, this.rememberEngineSelection);
    if (selectedEngine === this.state.selectedEngine) {
      this.closeEngineMenu();
      return;
    }
    this.engine?.selectEngine(selectedEngine);
    this.failedAnalysis = null;
    this.analysisPosition = null;
    this.setState({
      selectedEngine,
      engineMenuAnchorEl: null,
      engineUnlimited: false,
      bestMove: null,
      evals: [],
      engineError: '',
    }, () => {
      this.getScore();
    });
  };

  toggleAnalysisDepth = () => {
    if (
      !this._mounted ||
      !this.state.engineOn ||
      ['error', 'no-moves'].includes(this.state.engineStatus)
    ) return;

    this.setState(
      state => ({ engineUnlimited: !state.engineUnlimited }),
      () => this.getScore({ preserveEvals: true })
    );
  };
}
